import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button, Input, Space, Spin, Tag, Typography, message } from 'antd'
import {
  CheckCircleFilled,
  FileTextOutlined,
  LoadingOutlined,
  ReadOutlined,
  SendOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { adminApi } from '../api'
import { ChatMarkdown } from './ChatMarkdown'
import {
  failureFromErrorEvent,
  failureFromThrownError,
  stripWritingFence,
  streamDocWriting,
  writingContentForDisplay,
  WRITING_ERROR_SOURCE_LABEL,
  type DocWritingAction,
  type DocWritingErrorSource,
  type DocWritingFailure,
  type DocWritingFormat,
  type DocWritingMessage,
} from '../api/docWritingStream'

import { buildReadingWelcomeGreeting, buildReadingWelcomeSuggestions } from '../lib/docReadingWelcome'
import { buildWelcomeGreeting, buildWelcomeSuggestions } from '../lib/docWritingWelcome'
import type { SelectionAiAction } from './DocSelectionAiToolbar'
import './DocWritingAssistant.css'

const { Text } = Typography

export type DocInsertMode = 'cursor' | 'append' | 'replace' | 'afterSelection'

type AssistantMessage = DocWritingMessage & {
  sourceAction?: DocWritingAction
  hadSelection?: boolean
  isFailure?: boolean
  failureSource?: DocWritingErrorSource
  failureDetail?: string
}

function failureAssistantMessage(failure: DocWritingFailure): AssistantMessage {
  return {
    role: 'assistant',
    content: failure.message,
    failureDetail: failure.detail,
    isFailure: true,
    failureSource: failure.source,
  }
}

const ACTION_BUSY_LABEL: Record<DocWritingAction, string> = {
  outline: '正在生成大纲',
  continue: '正在续写正文',
  polish: '正在润色内容',
  expand: '正在扩写内容',
  chat: '正在撰写内容',
  ask: '正在分析文档',
}

const ACTION_PROMPTS: Record<Exclude<DocWritingAction, 'chat' | 'ask'>, (title: string) => string> = {
  outline: (title) => `请为${title.trim() ? `「${title.trim()}」` : '这篇文档'}生成大纲`,
  continue: () => '请根据选中内容续写，保持风格一致',
  polish: () => '请润色选中内容',
  expand: () => '请扩写选中内容',
}

const INSERT_SUCCESS: Record<DocInsertMode, string> = {
  cursor: '已插入光标处',
  append: '已追加到文末',
  replace: '已替换选中',
  afterSelection: '已插入选区后',
}

type StreamPhase = 'idle' | 'preparing' | 'thinking' | 'streaming'

type Props = {
  /** writing=编辑写作；reading=预览阅读问答 */
  variant?: 'writing' | 'reading'
  format: DocWritingFormat
  title: string
  category: string
  getDocumentContent: () => string
  getSelection: () => string
  onInsert: (content: string, mode: DocInsertMode, opts?: { pinnedSelection?: string }) => void
  onPinSelection?: (text: string) => void
  onLocateSelection?: () => void
  hideSelectionContext?: boolean
  disabled?: boolean
}

export type DocWritingAssistantHandle = {
  runQuickAction: (action: SelectionAiAction, opts?: { selection?: string; selectionPlain?: string }) => void
}

function phaseRank(phase: StreamPhase): number {
  if (phase === 'preparing') return 1
  if (phase === 'thinking') return 2
  if (phase === 'streaming') return 3
  return 0
}

function preferredInsertMode(msg: AssistantMessage): DocInsertMode {
  if (msg.sourceAction === 'continue' && msg.hadSelection) return 'afterSelection'
  if (msg.sourceAction === 'polish' || msg.sourceAction === 'expand') return 'replace'
  if (msg.sourceAction === 'chat' && msg.hadSelection) return 'replace'
  if (msg.sourceAction === 'outline') return 'append'
  if (msg.sourceAction === 'continue') return 'cursor'
  return 'cursor'
}

function renderInsertActions(
  content: string,
  msg: AssistantMessage,
  insertFrom: (content: string, mode: DocInsertMode) => void,
) {
  const preferred = preferredInsertMode(msg)
  const modes: DocInsertMode[] = ['replace', 'afterSelection', 'cursor', 'append']
  const labels: Record<DocInsertMode, string> = {
    replace: '替换选中',
    afterSelection: '插入选区后',
    cursor: '插入光标',
    append: '追加文末',
  }
  return (
    <Space size={4} wrap className="doc-writing-msg-actions">
      {modes.map((mode) => (
        <Button
          key={mode}
          type={mode === preferred ? 'link' : 'text'}
          size="small"
          className={mode === preferred ? 'doc-writing-msg-action--primary' : undefined}
          onClick={() => insertFrom(content, mode)}
        >
          {labels[mode]}
        </Button>
      ))}
    </Space>
  )
}

export const DocWritingAssistant = forwardRef<DocWritingAssistantHandle, Props>(function DocWritingAssistant(
  {
    variant = 'writing',
    format,
    title,
    category,
    getDocumentContent,
    getSelection,
    onInsert,
    onPinSelection,
    onLocateSelection,
    hideSelectionContext,
    disabled,
  },
  ref,
) {
  const isReading = variant === 'reading'
  const defaultAction: DocWritingAction = isReading ? 'ask' : 'chat'
  const [ready, setReady] = useState<boolean | null>(null)
  const [messages, setMessages] = useState<AssistantMessage[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [streamText, setStreamText] = useState('')
  const [streamAction, setStreamAction] = useState<DocWritingAction>('chat')
  const [streamPhase, setStreamPhase] = useState<StreamPhase>('idle')
  const [statusHint, setStatusHint] = useState('')
  const [waitSeconds, setWaitSeconds] = useState(0)
  const [pinnedSelection, setPinnedSelection] = useState('')
  const abortRef = useRef<AbortController | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const streamStartedRef = useRef(false)
  const turnMessagesRef = useRef<AssistantMessage[]>([])
  const lastSendMetaRef = useRef<{ action: DocWritingAction; hadSelection: boolean }>({
    action: 'chat',
    hadSelection: false,
  })

  useEffect(() => {
    adminApi
      .get<{ ready: boolean; message: string }>('/admin/knowledge/doc-writing/ready')
      .then((r) => setReady(r.data.ready))
      .catch(() => setReady(false))
  }, [])

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, streamText, streamPhase, waitSeconds])

  useEffect(() => {
    if (!streaming || streamPhase === 'streaming') {
      setWaitSeconds(0)
      return
    }
    const timer = window.setInterval(() => {
      setWaitSeconds((s) => s + 1)
    }, 1000)
    return () => window.clearInterval(timer)
  }, [streaming, streamPhase])

  const resetStreamState = useCallback(() => {
    setStreamText('')
    setStreamPhase('idle')
    setStatusHint('')
    setWaitSeconds(0)
    streamStartedRef.current = false
  }, [])

  const stopStream = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setStreaming(false)
    resetStreamState()
  }, [resetStreamState])

  const send = useCallback(
    async (
      action: DocWritingAction,
      text?: string,
      opts?: { selection?: string; selectionPlain?: string; skipUserBubble?: boolean; userDisplay?: string },
    ) => {
      const effectiveAction: DocWritingAction = isReading ? 'ask' : action
      const userMsg = (text ?? input).trim()
      if (!userMsg || streaming) return
      if (!ready) {
        const userBubbleEarly = (opts?.userDisplay ?? userMsg).trim() || userMsg
        const userMessageEarly: AssistantMessage = { role: 'user', content: userBubbleEarly }
        const failureReply = failureAssistantMessage({
          source: 'config',
          message: '写作助手未就绪，请先在「模型管理」中配置对话模型 API Key。',
        })
        setMessages((prev) => [...prev, userMessageEarly, failureReply])
        setInput('')
        return
      }

      const selectionText = isReading ? '' : (opts?.selection ?? getSelection()).trim()
      const selectionPlain = isReading ? '' : (opts?.selectionPlain ?? selectionText).trim()
      if (selectionText) {
        setPinnedSelection(selectionPlain || selectionText)
        onPinSelection?.(selectionPlain || selectionText)
        requestAnimationFrame(() => onLocateSelection?.())
      }
      lastSendMetaRef.current = { action: effectiveAction, hadSelection: !!selectionText }

      const userBubble = (opts?.userDisplay ?? userMsg).trim() || userMsg
      const userMessage: AssistantMessage = { role: 'user', content: userBubble }
      const historySnapshot = messages
      const nextHistory = opts?.skipUserBubble ? historySnapshot : [...historySnapshot, userMessage]
      turnMessagesRef.current = nextHistory
      if (!opts?.skipUserBubble) setMessages(nextHistory)
      setInput('')
      setStreaming(true)
      setStreamAction(effectiveAction)
      setStreamPhase('preparing')
      setStatusHint('已发送指令，正在连接模型…')
      setStreamText('')
      setWaitSeconds(0)
      streamStartedRef.current = false

      const apiUserMsg = userMsg

      const controller = new AbortController()
      abortRef.current = controller
      let acc = ''
      let gotReply = false

      const commitAssistantReply = (reply: AssistantMessage) => {
        if (gotReply) return
        gotReply = true
        const next = [...turnMessagesRef.current, reply]
        turnMessagesRef.current = next
        setMessages(next)
        setStreaming(false)
        resetStreamState()
      }

      const commitFailure = (failure: DocWritingFailure) => {
        commitAssistantReply(failureAssistantMessage(failure))
      }

      try {
        await streamDocWriting(
          {
            format,
            action: effectiveAction,
            user_message: apiUserMsg,
            title: title.trim(),
            category: category.trim(),
            selection: selectionText || undefined,
            document_excerpt: getDocumentContent().slice(0, 12000),
            messages: historySnapshot.slice(-18),
          },
          (ev) => {
            if (ev.type === 'status') {
              const phase = ev.phase as StreamPhase
              setStreamPhase((prev) => {
                const cur = prev === 'idle' ? 'preparing' : prev
                return phaseRank(phase) >= phaseRank(cur) ? phase : prev
              })
              if (ev.message) setStatusHint(ev.message)
              return
            }
            if (ev.type === 'content') {
              if (!streamStartedRef.current) {
                streamStartedRef.current = true
                setStreamPhase('streaming')
                setStatusHint('正在输出正文…')
              }
              acc += ev.text
              setStreamText(acc)
            } else if (ev.type === 'error') {
              commitFailure(failureFromErrorEvent(ev))
            } else if (ev.type === 'done') {
              const finalText = stripWritingFence((ev.content || acc).trim(), format)
              const meta = lastSendMetaRef.current
              if (finalText) {
                commitAssistantReply({
                  role: 'assistant',
                  content: finalText,
                  sourceAction: meta.action,
                  hadSelection: meta.hadSelection,
                })
              } else {
                commitFailure({
                  source: 'llm_empty',
                  message: '服务端返回空的完成事件，未包含正文内容。',
                  detail: 'done.content 与流式 content 均为空',
                })
              }
              if (selectionText) {
                requestAnimationFrame(() => onLocateSelection?.())
              }
            }
          },
          controller.signal,
        )
        if (!gotReply && acc.trim()) {
          const partial = stripWritingFence(acc.trim(), format)
          const meta = lastSendMetaRef.current
          commitAssistantReply({
            role: 'assistant',
            content: partial,
            sourceAction: meta.action,
            hadSelection: meta.hadSelection,
          })
        } else if (!gotReply && !acc.trim()) {
          commitFailure({
            source: 'frontend',
            message: '前端未收到完整流式响应（连接中断或未收到 done/error 事件）。',
            detail: 'stream closed without done, error, or content',
          })
        }
      } catch (e) {
        if ((e as Error).name === 'AbortError') {
          if (acc.trim()) {
            const partial = stripWritingFence(acc.trim(), format)
            const meta = lastSendMetaRef.current
            commitAssistantReply({
              role: 'assistant',
              content: partial,
              sourceAction: meta.action,
              hadSelection: meta.hadSelection,
            })
          } else {
            setStreaming(false)
            resetStreamState()
          }
        } else if (!gotReply) {
          commitFailure(failureFromThrownError(e))
        }
      } finally {
        setStreaming(false)
        abortRef.current = null
      }
    },
    [
      category,
      format,
      getDocumentContent,
      getSelection,
      input,
      isReading,
      messages,
      onLocateSelection,
      onPinSelection,
      ready,
      resetStreamState,
      streaming,
      title,
    ],
  )

  useImperativeHandle(
    ref,
    () => ({
      runQuickAction: (action, opts) => {
        if (isReading) return
        const selection = (opts?.selection ?? getSelection()).trim()
        const selectionPlain = (opts?.selectionPlain ?? selection).trim()
        const prompt =
          action === 'continue' && selection
            ? '请根据选中内容续写，保持风格一致'
            : action === 'continue'
              ? '请续写正文'
              : ACTION_PROMPTS[action](title)
        void send(action, prompt, { selection, selectionPlain, skipUserBubble: false })
      },
    }),
    [getSelection, isReading, send, title],
  )

  const insertFrom = (content: string, mode: DocInsertMode) => {
    const cleaned = stripWritingFence(content.trim(), format)
    if (!cleaned) {
      message.warning('没有可插入的内容')
      return
    }
    const pin = pinnedSelection.trim()
    if (pin && (mode === 'replace' || mode === 'afterSelection')) {
      onLocateSelection?.()
    }
    onInsert(cleaned, mode, pin ? { pinnedSelection: pin } : undefined)
    if (mode === 'replace' || mode === 'afterSelection') {
      setPinnedSelection('')
    }
    message.success(INSERT_SUCCESS[mode])
  }

  const liveSelection = getSelection().trim()
  const busyLabel = ACTION_BUSY_LABEL[streamAction]
  const outputChars = streamText.length
  const showStreamPanel = streaming
  const showWelcome = messages.length === 0 && !streaming
  const welcomeGreeting = useMemo(
    () =>
      isReading
        ? buildReadingWelcomeGreeting(title, category, format, getDocumentContent)
        : buildWelcomeGreeting(title, category, format, getDocumentContent),
    [title, category, format, getDocumentContent, isReading],
  )
  const welcomeSuggestions = useMemo(
    () =>
      isReading
        ? buildReadingWelcomeSuggestions(title, category, format, getDocumentContent)
        : buildWelcomeSuggestions(title, category, format, getDocumentContent),
    [title, category, format, getDocumentContent, isReading],
  )
  const welcomeSubject = title.trim() || category.trim()

  const renderMessageBody = (content: string, opts?: { live?: boolean }) => {
    const cls = opts?.live ? 'doc-writing-msg-body doc-writing-msg-body--live' : 'doc-writing-msg-body'
    if (!content.trim()) return <div className={cls} />
    if (isReading || format === 'markdown') {
      return (
        <div className={cls}>
          <ChatMarkdown content={content} className="doc-writing-markdown" />
        </div>
      )
    }
    return <div className={cls}>{writingContentForDisplay(content, format)}</div>
  }

  const stepState = (step: StreamPhase): 'done' | 'active' | 'pending' => {
    const current = streamPhase === 'idle' ? 'preparing' : streamPhase
    const order: StreamPhase[] = ['preparing', 'thinking', 'streaming']
    const idx = order.indexOf(step)
    const curIdx = order.indexOf(current)
    if (curIdx > idx) return 'done'
    if (curIdx === idx) return 'active'
    return 'pending'
  }

  return (
    <aside className={isReading ? 'doc-writing-aside doc-writing-aside--reading' : 'doc-writing-aside'}>
      <div className="doc-writing-aside-head">
        <Space size={8}>
          {isReading ? <ReadOutlined /> : <FileTextOutlined />}
          <Text strong>{isReading ? 'AI 阅读助手' : 'AI 写作助手'}</Text>
        </Space>
        {streaming ? (
          <Tag icon={<LoadingOutlined spin />} color="processing">
            {busyLabel}
          </Tag>
        ) : ready === false ? (
          <Tag color="warning">未配置模型</Tag>
        ) : null}
      </div>

      {!isReading && !hideSelectionContext && pinnedSelection ? (
        <div className="doc-writing-selection doc-writing-selection--pinned">
          <div className="doc-writing-selection-head">
            <Text type="secondary" className="doc-writing-selection-label">
              目标选区 · {pinnedSelection.length} 字
            </Text>
            {onLocateSelection ? (
              <Button type="link" size="small" className="doc-writing-selection-locate" onClick={onLocateSelection}>
                在正文定位
              </Button>
            ) : null}
          </div>
          <Text ellipsis className="doc-writing-selection-text">
            {pinnedSelection.slice(0, 120)}
          </Text>
        </div>
      ) : !isReading && !hideSelectionContext && liveSelection ? (
        <div className="doc-writing-selection">
          <Text type="secondary" className="doc-writing-selection-label">
            已选中 {liveSelection.length} 字
          </Text>
          <Text ellipsis className="doc-writing-selection-text">
            {liveSelection.slice(0, 120)}
          </Text>
        </div>
      ) : null}

      <div className="doc-writing-messages" ref={listRef}>
        {showWelcome ? (
          <div className="doc-writing-msg doc-writing-msg--ai doc-writing-msg--welcome">
            <div className="doc-writing-msg-body doc-writing-msg-body--greeting">{welcomeGreeting}</div>
            <div className="doc-writing-suggestions">
              {welcomeSuggestions.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className="doc-writing-suggestion"
                  disabled={disabled || !ready}
                  onClick={() => void send(defaultAction, item.prompt, { userDisplay: item.label })}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {messages.map((m, i) => (
          <div
            key={`${m.role}-${i}`}
            className={
              m.role === 'user' ? 'doc-writing-msg doc-writing-msg--user' : 'doc-writing-msg doc-writing-msg--ai'
            }
          >
            {m.role === 'user' ? (
              <div className="doc-writing-msg-body">{m.content}</div>
            ) : m.isFailure ? (
              <div className="doc-writing-msg-body">
                {m.failureSource ? (
                  <Text type="secondary" className="doc-writing-reply-source">
                    {WRITING_ERROR_SOURCE_LABEL[m.failureSource]}
                  </Text>
                ) : null}
                <div>{m.content}</div>
                {m.failureDetail ? (
                  <Text type="secondary" className="doc-writing-failure-detail">
                    {m.failureDetail}
                  </Text>
                ) : null}
              </div>
            ) : (
              renderMessageBody(m.content)
            )}
            {m.role === 'assistant' && !m.isFailure && !isReading
              ? renderInsertActions(m.content, m, insertFrom)
              : null}
          </div>
        ))}

        {showStreamPanel ? (
          <div className="doc-writing-msg doc-writing-msg--ai doc-writing-msg--streaming">
            <div className="doc-writing-progress">
              <div className="doc-writing-progress-title">{busyLabel}</div>
              <ul className="doc-writing-progress-steps">
                <li className={`doc-writing-progress-step doc-writing-progress-step--${stepState('preparing')}`}>
                  {stepState('preparing') === 'done' ? (
                    <CheckCircleFilled />
                  ) : stepState('preparing') === 'active' ? (
                    <LoadingOutlined spin />
                  ) : (
                    <span className="doc-writing-progress-dot" />
                  )}
                  <span>{isReading ? '理解你的问题' : '理解写作指令'}</span>
                </li>
                <li className={`doc-writing-progress-step doc-writing-progress-step--${stepState('thinking')}`}>
                  {stepState('thinking') === 'done' ? (
                    <CheckCircleFilled />
                  ) : stepState('thinking') === 'active' ? (
                    <LoadingOutlined spin />
                  ) : (
                    <span className="doc-writing-progress-dot" />
                  )}
                  <span>{isReading ? '分析文档内容' : '模型生成内容'}</span>
                </li>
                <li className={`doc-writing-progress-step doc-writing-progress-step--${stepState('streaming')}`}>
                  {stepState('streaming') === 'done' ? (
                    <CheckCircleFilled />
                  ) : stepState('streaming') === 'active' ? (
                    <LoadingOutlined spin />
                  ) : (
                    <span className="doc-writing-progress-dot" />
                  )}
                  <span>{isReading ? '生成回答' : '流式输出正文'}</span>
                </li>
              </ul>
              <Text type="secondary" className="doc-writing-progress-hint">
                {statusHint || '请稍候…'}
                {streamPhase !== 'streaming' && waitSeconds >= 2 ? `（已等待 ${waitSeconds} 秒）` : ''}
              </Text>
            </div>

            {streamText ? (
              renderMessageBody(streamText, { live: true })
            ) : (
              <div className="doc-writing-waiting">
                <Spin size="small" />
                <Text type="secondary">等待模型返回首段内容…</Text>
              </div>
            )}

            {outputChars > 0 ? (
              <Text type="secondary" className="doc-writing-stream-meta">
                已输出 {outputChars} 字
              </Text>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="doc-writing-input">
        <Input.TextArea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            isReading
              ? welcomeSubject
                ? `例如：这篇「${welcomeSubject}」主要说明了什么？有哪些结论？（Enter 发送）`
                : '例如：这篇文档主要讲了什么？有哪些关键要点？（Enter 发送）'
              : welcomeSubject
                ? `例如：写一份「${welcomeSubject}」的项目案例，800 字左右…`
                : '例如：写一份季度工作报告 / 产品宣传文案…（Enter 发送）'
          }
          autoSize={{ minRows: 2, maxRows: 5 }}
          disabled={disabled || streaming || !ready}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void send(defaultAction)
            }
          }}
        />
        <div className="doc-writing-input-actions">
          {streaming ? (
            <Space>
              <Text type="secondary" className="doc-writing-input-status">
                {streamPhase === 'streaming' && outputChars > 0
                  ? `生成中 · ${outputChars} 字`
                  : statusHint || `${busyLabel}…`}
              </Text>
              <Button icon={<StopOutlined />} onClick={stopStream}>
                停止
              </Button>
            </Space>
          ) : (
            <Button
              type="primary"
              icon={<SendOutlined />}
              disabled={disabled || !input.trim() || !ready}
              onClick={() => void send(defaultAction)}
            >
              发送
            </Button>
          )}
        </div>
      </div>
    </aside>
  )
})
